//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../../Config';
import { navigateTo } from '../../../utils/navigation';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Close } from '@carbon/icons-react';
import { cn } from '../../../lib/utils';
import WebhookDataService, {
  LoopTaskOption,
  SlackChannelOption,
  SlackConnectionOption,
  Webhook,
} from '../../../utils/WebhookDataService';
import { setPendingWebhookCreatedToast } from './webhookCreatedToast';

interface AddWebhookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  webhookService: WebhookDataService;
  loopId: string;
  teamspaceId: string;
  // Webhooks already registered for this loop — used for the client-side
  // duplicate-name check (defect fix: same-name webhooks were allowed).
  existingWebhooks: Webhook[];
  onWebhookCreated: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

// Sentinel value for "no output channel" (Radix Select cannot use an empty value).
const CHANNEL_NONE = 'none';
// Slack output goes through an installed Slack connection (Settings →
// Integrations) + a channel picked live from Slack.
const CHANNEL_SLACK = 'slack';
// Route of the Installed Integrations page (see App.tsx).
const INTEGRATIONS_ROUTE = '/integration';

// Name rules mirror the User Groups "Group Name" convention: letters, numbers,
// and underscores only (no spaces/special characters), capped at 20 chars.
const NAME_MAX_LENGTH = 20;
const DESCRIPTION_MAX_LENGTH = 100;
// Allows an empty string too, so it can gate onChange while the user is still typing/deleting.
const NAME_CHAR_PATTERN = /^[a-zA-Z0-9_]*$/;
const NAME_PATTERN = /^[a-zA-Z0-9_]+$/;

const AddWebhookDialog: React.FC<AddWebhookDialogProps> = ({
  open,
  onOpenChange,
  webhookService,
  loopId,
  teamspaceId,
  existingWebhooks,
  onWebhookCreated,
  onError,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { oidc, baseName } = useConfig();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hmacKey, setHmacKey] = useState('');
  const [taskId, setTaskId] = useState('');
  const [channelType, setChannelType] = useState<string>(CHANNEL_NONE);
  const [tasks, setTasks] = useState<LoopTaskOption[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  // Slack output: installed connections (loaded when the panel opens so the
  // Slack option can be disabled up-front) and the chosen connection's channels.
  const [slackConnections, setSlackConnections] = useState<
    SlackConnectionOption[]
  >([]);
  const [slackConnectionsLoading, setSlackConnectionsLoading] = useState(false);
  const [slackConnectionsError, setSlackConnectionsError] = useState(false);
  const [slackConnectionId, setSlackConnectionId] = useState('');
  const [slackChannels, setSlackChannels] = useState<SlackChannelOption[]>([]);
  const [slackChannelsLoading, setSlackChannelsLoading] = useState(false);
  const [slackChannelsError, setSlackChannelsError] = useState(false);
  const [slackChannelId, setSlackChannelId] = useState('');
  // Bumped by the Retry button to re-run the channel fetch effect.
  const [slackChannelsAttempt, setSlackChannelsAttempt] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');
  const [specialCharWarning, setSpecialCharWarning] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const specialCharTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // Synchronous re-entrancy guard for handleSubmit. `isSubmitting` state alone
  // isn't enough: a second click fired before React re-renders (and disables
  // the button) would race past it, creating duplicate webhooks. A ref updates
  // immediately, closing that window.
  const isSubmittingRef = useRef(false);

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setHmacKey('');
    setTaskId('');
    setChannelType(CHANNEL_NONE);
    setSlackConnectionId('');
    setSlackChannels([]);
    setSlackChannelsError(false);
    setSlackChannelId('');
    setNameError('');
    setSpecialCharWarning('');
    setIsGenerating(false);
    setIsSubmitting(false);
    isSubmittingRef.current = false;
  }, []);

  // Cleanup the transient special-character warning timer on unmount.
  useEffect(() => {
    return () => {
      if (specialCharTimerRef.current) {
        clearTimeout(specialCharTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (open) {
      resetForm();
      // Move focus into the panel when it opens (WCAG 2.4.3)
      setTimeout(() => nameInputRef.current?.focus(), 0);
    }
  }, [open, resetForm]);

  // Load the loop's available tasks when the panel opens.
  useEffect(() => {
    if (!open || !loopId || !teamspaceId) return;
    let cancelled = false;
    setTasksLoading(true);
    webhookService
      .listLoopTasks(teamspaceId, loopId)
      .then((data) => {
        if (!cancelled) setTasks(data);
      })
      .catch(() => {
        if (!cancelled) onError(t('webhook-tasks-fetch-failed'));
      })
      .finally(() => {
        if (!cancelled) setTasksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, loopId, teamspaceId, webhookService, onError, t]);

  // Load the teamspace's Slack connections when the panel opens. Done up-front
  // (not on Slack selection) so the Slack option can be disabled, with a hint
  // pointing to Integrations, when nothing is connected yet.
  useEffect(() => {
    if (!open || !teamspaceId) return;
    let cancelled = false;
    setSlackConnectionsLoading(true);
    setSlackConnectionsError(false);
    webhookService
      .listSlackConnections(teamspaceId)
      .then((data) => {
        if (!cancelled) setSlackConnections(data);
      })
      .catch(() => {
        if (!cancelled) {
          setSlackConnections([]);
          setSlackConnectionsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setSlackConnectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, teamspaceId, webhookService]);

  // Leaving Slack clears the Slack selection so a stale connection/channel is
  // never submitted with another channel type.
  useEffect(() => {
    if (channelType !== CHANNEL_SLACK) {
      setSlackConnectionId('');
      setSlackChannelId('');
      setSlackChannels([]);
      setSlackChannelsError(false);
    }
  }, [channelType]);

  // Load the channels visible to the chosen Slack connection (live from Slack),
  // keeping only channels the bot has joined. Public channels it has not joined
  // are technically postable via chat:write.public, but offering them lets an
  // admin route output somewhere the bot was never invited — so they are hidden
  // and the empty state tells the admin to /invite the bot first.
  // Changing the connection clears the channel; Retry bumps slackChannelsAttempt.
  useEffect(() => {
    setSlackChannelId('');
    if (channelType !== CHANNEL_SLACK || !slackConnectionId || !teamspaceId) {
      setSlackChannels([]);
      setSlackChannelsError(false);
      return;
    }
    let cancelled = false;
    setSlackChannelsLoading(true);
    setSlackChannelsError(false);
    webhookService
      .listSlackChannels(teamspaceId, slackConnectionId)
      .then((data) => {
        if (!cancelled) setSlackChannels(data.filter((c) => c.is_member));
      })
      .catch(() => {
        if (!cancelled) {
          setSlackChannels([]);
          setSlackChannelsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setSlackChannelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    channelType,
    slackConnectionId,
    teamspaceId,
    webhookService,
    slackChannelsAttempt,
  ]);

  // Slack is selectable only when at least one connection has a bot token
  // saved — that is the credential the webhook posts with.
  const hasSelectableSlackConnection = slackConnections.some(
    (c) => c.has_bot_token,
  );
  const slackDisabled =
    slackConnectionsLoading ||
    slackConnectionsError ||
    !hasSelectableSlackConnection;

  const handleChannelTypeChange = useCallback(
    (value: string) => {
      // Defensive: a disabled Radix item cannot be picked, but never let a
      // Slack selection through when there is nothing to post with.
      if (value === CHANNEL_SLACK && slackDisabled) return;
      setChannelType(value);
    },
    [slackDisabled],
  );

  const handleGoToIntegrations = useCallback(() => {
    // Strip trailing slashes without a regex: `/\/+$/` is flagged by Sonar
    // (S5852) as backtracking-prone, and a plain loop is linear and clearer.
    let base = baseName || '';
    while (base.endsWith('/')) base = base.slice(0, -1);
    navigateTo(`${base}${INTEGRATIONS_ROUTE}`.replace(/\/\/+/g, '/'));
  }, [baseName]);

  const handleRetrySlackChannels = useCallback(() => {
    setSlackChannelsAttempt((n) => n + 1);
  }, []);

  // Only joined channels are listed, so the sole annotation is "private".
  const slackChannelLabel = useCallback(
    (channel: SlackChannelOption) =>
      channel.is_private
        ? `#${channel.name} (${t('webhook-slack-channel-private')})`
        : `#${channel.name}`,
    [t],
  );

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      resetForm();
      onOpenChange(false);
    }
  }, [isSubmitting, resetForm, onOpenChange]);

  const handleGenerateKey = useCallback(async () => {
    setIsGenerating(true);
    try {
      const response = await webhookService.generateKey();
      setHmacKey(response.hmac_key);
    } catch {
      onError(t('webhook-generate-key-failed'));
    } finally {
      setIsGenerating(false);
    }
  }, [webhookService, onError, t]);

  const validateName = useCallback(
    (value: string): boolean => {
      const trimmed = value.trim();
      if (!trimmed) {
        setNameError(t('webhook-name-required'));
        return false;
      }
      if (trimmed.length > NAME_MAX_LENGTH) {
        setNameError(t('webhook-name-too-long'));
        return false;
      }
      if (!NAME_PATTERN.test(trimmed)) {
        setNameError(t('webhook-name-special-chars'));
        return false;
      }
      setNameError('');
      return true;
    },
    [t],
  );

  // Reject disallowed characters at the keystroke (matches the Group Name
  // convention): the field simply doesn't update rather than accepting and
  // then flagging invalid input.
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      if (!NAME_CHAR_PATTERN.test(input)) {
        setSpecialCharWarning(t('webhook-name-special-chars'));
        if (specialCharTimerRef.current) {
          clearTimeout(specialCharTimerRef.current);
        }
        specialCharTimerRef.current = setTimeout(() => {
          setSpecialCharWarning('');
          specialCharTimerRef.current = null;
        }, 2000);
        return;
      }
      setSpecialCharWarning('');
      setName(input);
      if (nameError) validateName(input);
    },
    [nameError, t, validateName],
  );

  // Inline duplicate-name feedback while typing (debounced). handleSubmit
  // below re-checks synchronously before creating — this is UX only.
  useEffect(() => {
    const trimmed = name.trim();
    if (
      !trimmed ||
      trimmed.length > NAME_MAX_LENGTH ||
      !NAME_PATTERN.test(trimmed)
    ) {
      return;
    }
    const timeoutId = setTimeout(() => {
      const isDuplicate = existingWebhooks.some(
        (wh) => wh.name.toLowerCase() === trimmed.toLowerCase(),
      );
      setNameError(isDuplicate ? t('webhook-name-exists') : '');
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [name, existingWebhooks, t]);

  const handleSubmit = useCallback(async () => {
    // Closes the double-submit window: a second click before React disables
    // the button (or a double-dispatched event) returns immediately here.
    if (isSubmittingRef.current) return;
    if (!validateName(name)) return;

    const trimmedName = name.trim();
    const isDuplicate = existingWebhooks.some(
      (wh) => wh.name.toLowerCase() === trimmedName.toLowerCase(),
    );
    if (isDuplicate) {
      setNameError(t('webhook-name-exists'));
      return;
    }

    if (!taskId) {
      onError(t('webhook-task-required'));
      return;
    }
    if (!hmacKey) {
      onError(t('webhook-generate-key-first'));
      return;
    }
    const isSlack = channelType === CHANNEL_SLACK;
    if (isSlack && !slackConnectionId) {
      onError(t('webhook-slack-connection-required'));
      return;
    }
    if (isSlack && !slackChannelId) {
      onError(t('webhook-slack-channel-required'));
      return;
    }
    const selectedSlackChannel = isSlack
      ? slackChannels.find((c) => c.id === slackChannelId)
      : undefined;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const created = await webhookService.createWebhook(loopId, teamspaceId, {
        name: name.trim(),
        description: description.trim() || undefined,
        hmac_key: hmacKey,
        task_name: taskId,
        output_channel_type: isSlack ? CHANNEL_SLACK : undefined,
        slack_connection_id: isSlack ? slackConnectionId : undefined,
        slack_channel_id: isSlack ? slackChannelId : undefined,
        slack_channel_name: isSlack ? selectedSlackChannel?.name : undefined,
      });

      // The webhook is not usable until OAuth completes. If the backend returned
      // an authorization URL, redirect the browser to it; Keycloak will redirect
      // back to the webhook list once the user consents. React state (and any
      // toast) is lost on that round trip, so stash the success message for
      // WebhooksTab to show once it lands back.
      if (created?.oauth_url) {
        const allowedOrigin = new URL(oidc.authorization).origin;
        const redirectUrl = new URL(created.oauth_url);
        if (redirectUrl.origin !== allowedOrigin) {
          throw new Error('Unexpected OAuth redirect origin');
        }
        setPendingWebhookCreatedToast(
          t('webhook-created-successfully', { name: trimmedName }),
        );
        navigateTo(created.oauth_url);
        return;
      }

      // OAuth could not be initiated (not configured). The webhook exists but
      // shows "Authorization required"; the user can authorize it from the list.
      onSuccess(t('webhook-created-auth-pending', { name: trimmedName }));
      onWebhookCreated();
      onOpenChange(false);
    } catch (error: any) {
      // A concurrent create (e.g. two tabs, or the same race this fix closes
      // elsewhere) can still lose to the server-side uniqueness check —
      // surface it as an inline name error rather than a generic toast.
      if (error?.data?.error === 'duplicate_name') {
        setNameError(t('webhook-name-exists'));
      } else {
        onError(t('webhook-creation-failed'));
      }
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  }, [
    name,
    description,
    hmacKey,
    taskId,
    channelType,
    slackConnectionId,
    slackChannelId,
    slackChannels,
    loopId,
    teamspaceId,
    existingWebhooks,
    webhookService,
    validateName,
    onError,
    onSuccess,
    onWebhookCreated,
    onOpenChange,
    t,
  ]);

  const channelSelectionIncomplete =
    channelType === CHANNEL_SLACK && (!slackConnectionId || !slackChannelId);

  const isSubmitDisabled =
    !name.trim() ||
    !!nameError ||
    !taskId ||
    !hmacKey ||
    channelSelectionIncomplete ||
    isSubmitting;

  const inputClasses =
    'w-full bg-white dark:bg-[#262626] border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 placeholder-gray-400';

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        id="id-add-webhook-overlay"
        data-testid="add-webhook-backdrop"
        role="presentation"
        className="fixed inset-0 top-12 z-40 bg-black/20"
        onClick={handleClose}
      />

      {/* Panel */}
      <div
        id="id-add-webhook-panel"
        data-testid="add-webhook-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-webhook-panel-title"
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') handleClose();
        }}
        className="fixed bottom-0 right-0 top-12 z-50 flex w-[502px] flex-col border-l border-[#d3d9de] bg-white shadow-xl dark:border-gray-700 dark:bg-[#262626]"
      >
        {/* Header */}
        <div
          id="id-add-webhook-header"
          data-testid="add-webhook-header"
          className="flex items-center justify-between border-b border-gray-200 bg-[#f2f4f5] p-4 dark:border-gray-700 dark:bg-[#393939]"
        >
          <h2
            id="add-webhook-panel-title"
            className="text-[16px] font-semibold text-gray-700 dark:text-gray-300"
          >
            {t('webhook-add-title')}
          </h2>
          <button
            id="id-add-webhook-close"
            data-testid="add-webhook-close"
            onClick={handleClose}
            className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
            disabled={isSubmitting}
            aria-label={t('close')}
          >
            <Close size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div
          id="id-add-webhook-content"
          data-testid="add-webhook-content"
          className="flex-1 overflow-y-auto p-6"
        >
          <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
            {/* Name */}
            <div
              className="space-y-2"
              id="id-webhook-name-field"
              data-testid="webhook-name-field"
            >
              <Label
                htmlFor="id-webhook-name"
                className="text-gray-700 dark:text-gray-300"
              >
                {t('name')} <span className="text-red-500">*</span>
              </Label>
              <Input
                ref={nameInputRef}
                type="text"
                id="id-webhook-name"
                data-testid="webhook-name-input"
                className={cn(
                  inputClasses,
                  nameError && 'border-red-500 focus-visible:ring-red-500',
                )}
                placeholder={t('webhook-name-placeholder')}
                value={name}
                onChange={handleNameChange}
                maxLength={NAME_MAX_LENGTH}
                required
                aria-invalid={!!nameError}
                aria-describedby={
                  nameError ? 'webhook-name-error' : 'webhook-name-help'
                }
              />
              <p
                id="webhook-name-counter"
                className="text-xs text-gray-500 dark:text-gray-400"
              >
                {name.length}/{NAME_MAX_LENGTH} characters
              </p>
              {specialCharWarning && (
                <p
                  className="text-xs italic text-red-500"
                  role="alert"
                  data-testid="webhook-name-special-char-warning"
                >
                  {specialCharWarning}
                </p>
              )}
              {nameError ? (
                <p
                  id="webhook-name-error"
                  className="text-xs text-red-500"
                  role="alert"
                >
                  {nameError}
                </p>
              ) : (
                <p
                  id="webhook-name-help"
                  className="text-xs text-gray-500 dark:text-gray-400"
                >
                  {t('webhook-name-help')}
                </p>
              )}
            </div>

            {/* Description */}
            <div
              className="space-y-2"
              id="id-webhook-description-field"
              data-testid="webhook-description-field"
            >
              <Label
                htmlFor="id-webhook-description"
                className="text-gray-700 dark:text-gray-300"
              >
                {t('description-optional')}
              </Label>
              <Input
                type="text"
                id="id-webhook-description"
                data-testid="webhook-description-input"
                className={inputClasses}
                placeholder={t('webhook-description-placeholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={DESCRIPTION_MAX_LENGTH}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {description.length}/{DESCRIPTION_MAX_LENGTH} characters
              </p>
            </div>

            {/* Task */}
            <div
              className="space-y-2"
              id="id-webhook-task-field"
              data-testid="webhook-task-field"
            >
              <Label
                htmlFor="id-webhook-task"
                className="text-gray-700 dark:text-gray-300"
              >
                {t('webhook-task')} <span className="text-red-500">*</span>
              </Label>
              <Select
                value={taskId}
                onValueChange={setTaskId}
                disabled={tasksLoading || tasks.length === 0}
              >
                <SelectTrigger
                  id="id-webhook-task"
                  data-testid="webhook-task-select"
                  aria-describedby="webhook-task-help"
                >
                  <SelectValue
                    placeholder={
                      tasksLoading
                        ? t('webhook-tasks-loading')
                        : tasks.length === 0
                          ? t('webhook-tasks-empty')
                          : t('webhook-task-placeholder')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {tasks.map((task) => (
                    <SelectItem
                      key={task.id}
                      value={task.id}
                      data-testid={`webhook-task-option-${task.id}`}
                    >
                      {task.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p
                id="webhook-task-help"
                className="text-xs text-gray-500 dark:text-gray-400"
              >
                {t('webhook-task-help')}
              </p>
            </div>

            {/* HMAC Key */}
            <div
              className="space-y-2"
              id="id-webhook-hmac-key-field"
              data-testid="webhook-hmac-key-field"
            >
              <Label
                htmlFor="id-webhook-hmac-key"
                className="text-gray-700 dark:text-gray-300"
              >
                {t('webhook-hmac-key')} <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  id="id-webhook-hmac-key"
                  data-testid="webhook-hmac-key-input"
                  className={cn(inputClasses, 'flex-1 font-mono text-xs')}
                  value={hmacKey}
                  readOnly
                  placeholder={t('webhook-hmac-key-placeholder')}
                  aria-describedby="webhook-hmac-key-help"
                />
                <Button
                  type="button"
                  variant="outline"
                  id="id-webhook-generate-key"
                  data-testid="webhook-generate-key-button"
                  onClick={handleGenerateKey}
                  disabled={isGenerating}
                  className="shrink-0 border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {isGenerating
                    ? t('webhook-generating')
                    : t('webhook-generate')}
                </Button>
              </div>
              <p
                id="webhook-hmac-key-help"
                className="text-xs text-gray-500 dark:text-gray-400"
              >
                {t('webhook-hmac-key-help')}
              </p>
            </div>

            {/* Output Channel */}
            <div
              className="space-y-2"
              id="id-webhook-channel-field"
              data-testid="webhook-channel-field"
            >
              <Label
                htmlFor="id-webhook-channel"
                className="text-gray-700 dark:text-gray-300"
              >
                {t('webhook-output-channel')}
              </Label>
              <Select
                value={channelType}
                onValueChange={handleChannelTypeChange}
              >
                <SelectTrigger
                  id="id-webhook-channel"
                  data-testid="webhook-channel-select"
                  aria-describedby="webhook-channel-help"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CHANNEL_NONE}>
                    {t('webhook-channel-none')}
                  </SelectItem>
                  <SelectItem
                    value={CHANNEL_SLACK}
                    disabled={slackDisabled}
                    aria-disabled={slackDisabled || undefined}
                    data-testid="webhook-channel-option-slack"
                  >
                    {t('webhook-channel-slack')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p
                id="webhook-channel-help"
                className="text-xs text-gray-500 dark:text-gray-400"
              >
                {t('webhook-output-channel-help')}
              </p>
              {/* Slack unavailable — explain why and link to Integrations */}
              {!slackConnectionsLoading && slackDisabled && (
                <p
                  id="webhook-slack-not-connected"
                  data-testid="webhook-slack-not-connected"
                  className="text-xs text-gray-500 dark:text-gray-400"
                >
                  {slackConnectionsError
                    ? t('webhook-slack-connections-failed')
                    : t('webhook-slack-not-connected')}{' '}
                  <button
                    type="button"
                    data-testid="webhook-slack-go-to-integrations"
                    onClick={handleGoToIntegrations}
                    className="text-[#0F62FE] underline hover:text-[#0353E9] dark:text-[#78a9ff]"
                  >
                    {t('webhook-slack-go-to-integrations')}
                  </button>
                </p>
              )}
            </div>

            {/* Slack connection — only when Slack is selected */}
            {channelType === CHANNEL_SLACK && (
              <div
                className="space-y-2"
                id="id-webhook-slack-connection-field"
                data-testid="webhook-slack-connection-field"
              >
                <Label
                  htmlFor="id-webhook-slack-connection"
                  className="text-gray-700 dark:text-gray-300"
                >
                  {t('webhook-slack-connection')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={slackConnectionId}
                  onValueChange={setSlackConnectionId}
                  disabled={slackConnectionsLoading}
                >
                  <SelectTrigger
                    id="id-webhook-slack-connection"
                    data-testid="webhook-slack-connection-select"
                    aria-describedby="webhook-slack-connection-help"
                  >
                    <SelectValue
                      placeholder={
                        slackConnectionsLoading
                          ? t('webhook-slack-connections-loading')
                          : t('webhook-slack-connection-placeholder')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {slackConnections.map((connection) => (
                      <SelectItem
                        key={connection.connection_id}
                        value={connection.connection_id}
                        disabled={!connection.has_bot_token}
                        aria-disabled={!connection.has_bot_token || undefined}
                        data-testid={`webhook-slack-connection-option-${connection.connection_id}`}
                      >
                        {connection.has_bot_token
                          ? connection.name
                          : `${connection.name} (${t('webhook-slack-connection-no-token')})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p
                  id="webhook-slack-connection-help"
                  className="text-xs text-gray-500 dark:text-gray-400"
                >
                  {t('webhook-slack-connection-help')}
                </p>
              </div>
            )}

            {/* Slack channel — once a connection is chosen */}
            {channelType === CHANNEL_SLACK && slackConnectionId && (
              <div
                className="space-y-2"
                id="id-webhook-slack-channel-field"
                data-testid="webhook-slack-channel-field"
              >
                <Label
                  htmlFor="id-webhook-slack-channel"
                  className="text-gray-700 dark:text-gray-300"
                >
                  {t('webhook-slack-channel')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={slackChannelId}
                  onValueChange={setSlackChannelId}
                  disabled={
                    slackChannelsLoading ||
                    slackChannelsError ||
                    slackChannels.length === 0
                  }
                >
                  <SelectTrigger
                    id="id-webhook-slack-channel"
                    data-testid="webhook-slack-channel-select"
                    aria-describedby="webhook-slack-channel-help"
                  >
                    <SelectValue
                      placeholder={
                        slackChannelsLoading
                          ? t('webhook-slack-channels-loading')
                          : slackChannelsError
                            ? t('webhook-slack-channels-failed')
                            : slackChannels.length === 0
                              ? t('webhook-slack-channels-empty')
                              : t('webhook-slack-channel-placeholder')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {slackChannels.map((channel) => (
                      <SelectItem
                        key={channel.id}
                        value={channel.id}
                        data-testid={`webhook-slack-channel-option-${channel.id}`}
                      >
                        {slackChannelLabel(channel)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {slackChannelsError ? (
                  <p
                    className="text-xs text-red-500"
                    role="alert"
                    data-testid="webhook-slack-channels-error"
                  >
                    {t('webhook-slack-channels-failed')}{' '}
                    <button
                      type="button"
                      data-testid="webhook-slack-channels-retry"
                      onClick={handleRetrySlackChannels}
                      className="underline"
                    >
                      {t('webhook-slack-retry')}
                    </button>
                  </p>
                ) : (
                  <p
                    id="webhook-slack-channel-help"
                    className="text-xs text-gray-500 dark:text-gray-400"
                  >
                    {t('webhook-slack-channel-help')}
                  </p>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div
          id="id-add-webhook-footer"
          data-testid="add-webhook-footer"
          className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700"
        >
          <Button
            id="id-webhook-cancel"
            data-testid="webhook-cancel-button"
            onClick={handleClose}
            variant="outline"
            disabled={isSubmitting}
            className="w-[100px] border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('cancel')}
          </Button>
          <Button
            id="id-webhook-save"
            data-testid="webhook-add-button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="min-w-[140px] rounded bg-[#0F62FE] text-white hover:bg-[#0353E9] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#0F62FE] dark:hover:bg-[#4589ff]"
          >
            {isSubmitting
              ? t('webhook-adding')
              : t('webhook-save-and-authorize')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddWebhookDialog;
